import './book-event-modal.scss';
import { toast } from 'react-toastify';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useCloseOldModal,
  useModalData,
  useSetUnmountModalCallback,
} from '../../../store/modal/modalHooks';
import {
  useBookEvent,
  useJoinWaitingList,
  useStartOrderCancellationProcess,
} from '../../../store/events/eventHooks';
import { useTranslation } from 'react-i18next';
import PaymentIframe from '../../shared/PaymentIframe';
import { Event } from '../../../store/events/eventTypes';
import { useHistory, useLocation } from 'react-router-dom';
import BookEventInfo from '../../pages/book-events/BookEventInfo';
import BookEventForm from '../../pages/book-events/BookEventForm';
import { useCreditInUse } from '../../../store/credits/creditsHooks';
import BookingSelectCreditCard from '../../shared/BookingSelectCreditCard';
import BookSuccessMessage from '../../pages/book-events/BookSuccessMessage';
import CancellationPolicy from '../../shared/cancellation-policy/CancellationPolicy';
import BookWaitingListSuccessMessage from '../../pages/book-events/BookWaitingListSuccessMessage';
import BookModalComponent from '../../shared/book-components/BookModalComponent/BookModalComponent';
import { BookFormPhaseEnum } from '../../shared/book-components/BookModalComponent/book-form-phase.enum';
import { useGrowPayment } from '../../../utility/hooks/useGrowPayment';

const BookEventModalContainer = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const modalData = useModalData();
  const closeModal = useCloseOldModal();
  const creditInUse = useCreditInUse();
  const setUnmountModalCallback = useSetUnmountModalCallback();
  const { openPayment, closePayment } = useGrowPayment();
  const [isPaid, setIsPaid] = useState(false);
  const isPaidRef = useRef(false);

  const bookEvent = useBookEvent();
  const startOrderCancellationProcess = useStartOrderCancellationProcess();
  const joinWaitingList = useJoinWaitingList();

  const [showPolicy, setShowPolicy] = useState(false);
  const [iframePaymentUrl, setIframePaymentUrl] = useState<string>();
  const [bookFormPhase, setBookFormPhase] = useState<BookFormPhaseEnum>(
    BookFormPhaseEnum.FORM_PAGE
  );

  useEffect(() => {
    isPaidRef.current = isPaid;
  }, [isPaid]);

  const event: Event = modalData?.data.event;

  if (!event) {
    closeModal();
  }

  const onClickClosePayment = (orderId: number) => {
    startOrderCancellationProcess(event.id, orderId);
    closeModal();
  };

  const onClickBookEvent = async () => {
    if (creditInUse.cashAmount) {
      setBookFormPhase(BookFormPhaseEnum.CREDIT_CARD_PAGE);
    } else {
      await onBookEvent();
    }
  };

  const onBookPendingPayment = async () => {
    try {
      const response = await bookEvent(event, location.pathname, true);
      if (response.isPaid) {
        setBookFormPhase(BookFormPhaseEnum.SUCCESS_PAGE);
      }

      if (response.paymentProcessLink) {
        setIframePaymentUrl(response.paymentProcessLink);
        setBookFormPhase(BookFormPhaseEnum.PAYMENT_PAGE);
      }
      if (response.authCode) {
        onHandleOpenPayment(
          response.orderId,
          response.authCode,
          isNaN(Number(response.authCodeExpiration))
            ? undefined
            : Number(response.authCodeExpiration)
        );
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      if (errorMessage) {
        onFailure(errorMessage);
      }
    }
  };

  const onHandleOpenPayment = (
    orderId: number,
    authCode: string,
    authCodeExpiration?: number
  ) => {
    const expirationInSeconds = authCodeExpiration
      ? Math.floor(authCodeExpiration - Date.now())
      : 300; // 5 min in seconds

    const timeout = setTimeout(() => {
      closePayment();
      if (!isPaidRef.current) {
        onClickClosePayment(orderId);
      }
    }, expirationInSeconds * 1000);

    openPayment(authCode, {
      onSuccess: (response: any) => {
        setIsPaid(true);
        setBookFormPhase(BookFormPhaseEnum.SUCCESS_PAGE);
        clearTimeout(timeout);
      },
      onFailure: (response: { message: string }) => {
        toast.error(response.message, {
          icon: false,
          hideProgressBar: true,
          autoClose: 5000,
        });
        clearTimeout(timeout);
        if (!isPaidRef.current) {
          onClickClosePayment(orderId);
        }
      },
      onWalletChange: (response: any) => {
        if (response === 'open') {
          setIsPaid(false);
        }
        if (response === 'close') {
          if (!isPaidRef.current) {
            onClickClosePayment(orderId);
          }
        }
      },
    });
  };

  const onBookEvent = async () => {
    try {
      const response = await bookEvent(event, location.pathname);
      if (response.isPaid) {
        setBookFormPhase(BookFormPhaseEnum.SUCCESS_PAGE);
      }

      if (response.paymentProcessLink) {
        setIframePaymentUrl(response.paymentProcessLink);
        setBookFormPhase(BookFormPhaseEnum.PAYMENT_PAGE);
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      if (errorMessage) {
        onFailure(errorMessage);
      }
    }
  };

  const onJoinWaitingList = async () => {
    try {
      await joinWaitingList(event);
      setBookFormPhase(BookFormPhaseEnum.WAITING_LIST_SUCCESS_PAGE);
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      if (errorMessage) {
        onFailure(errorMessage);
      }
    }
  };

  const onFailure = (errorMessage: string) => {
    toast.error(errorMessage, {
      icon: false,
      hideProgressBar: true,
      autoClose: 5000,
    });
    closeModal();
  };

  const InfoComponent = useMemo(() => {
    if (showPolicy) {
      return <CancellationPolicy policyId={event.policyId} />;
    }
    return <BookEventInfo event={event} />;
  }, [showPolicy]);

  const FormComponent = useMemo(() => {
    switch (bookFormPhase) {
      case BookFormPhaseEnum.FORM_PAGE:
        return (
          <BookEventForm
            event={event}
            onBookEvent={onClickBookEvent}
            onClickJoinWaitingList={onJoinWaitingList}
            onSwitchShowPolicy={() => setShowPolicy(!showPolicy)}
            showPolicy={showPolicy}
          />
        );

      case BookFormPhaseEnum.CREDIT_CARD_PAGE:
        return (
          <BookingSelectCreditCard
            onClickSelectCreditCard={onBookEvent}
            onClickSelectNewCreditCard={onBookPendingPayment}
            onClickBack={() => setBookFormPhase(BookFormPhaseEnum.FORM_PAGE)}
          />
        );

      case BookFormPhaseEnum.PAYMENT_PAGE:
        return (
          <div className="book-event-modal-iframe">
            <PaymentIframe
              url={iframePaymentUrl}
              backText={t('BACK_BUTTON_TEXT')}
              onClickBack={() => {
                setIframePaymentUrl(undefined);
                setBookFormPhase(BookFormPhaseEnum.FORM_PAGE);
              }}
              onPaymentSuccess={() => {
                setBookFormPhase(BookFormPhaseEnum.SUCCESS_PAGE);
                setUnmountModalCallback(undefined);
              }}
            />
          </div>
        );

      case BookFormPhaseEnum.SUCCESS_PAGE:
        return (
          <BookSuccessMessage
            onClick={() => {
              closeModal();
              history.push('/my-activities');
            }}
          />
        );

      case BookFormPhaseEnum.WAITING_LIST_SUCCESS_PAGE:
        return (
          <BookWaitingListSuccessMessage
            onClick={() => {
              closeModal();
              history.push('/my-activities');
            }}
          />
        );
    }
  }, [bookFormPhase, showPolicy, creditInUse]);

  return (
    <div className="book-event-modal">
      <BookModalComponent
        infoComponent={InfoComponent}
        formComponent={FormComponent}
      />
    </div>
  );
};

export default BookEventModalContainer;
